import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { BigButton } from "../design/BigButton";

function statusBadge(lvl) {
  if (lvl.isPublished) return <span className="admin-badge admin-badge--published">published</span>;
  const s = lvl.contentStatus || "draft";
  const cls = s === "polished" ? "admin-badge--polished" : s === "playable" ? "admin-badge--playable" : "admin-badge--draft";
  return <span className={`admin-badge ${cls}`}>{s}</span>;
}

export function WorldManager() {
  const [worlds, setWorlds] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // world being edited

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/worlds");
      setWorlds(data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    try {
      await api.patch(`/admin/worlds/${editing.id}`, {
        name: editing.name, description: editing.description,
        sortOrder: editing.sortOrder, requiredStars: editing.requiredStars,
        thumbnailUrl: editing.thumbnailUrl, mapArtworkUrl: editing.mapArtworkUrl,
        isPublished: editing.isPublished,
      });
      setEditing(null);
      reload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  return (
    <div data-testid="world-manager">
      <div className="admin-header">
        <div><h1>Worlds</h1><p className="admin-header__sub">{worlds.length} worlds</p></div>
      </div>
      {err && <div className="admin-error">{err}</div>}
      {loading ? <p>Loading…</p> : (
        <table className="admin-table">
          <thead><tr><th>Order</th><th>Name</th><th>Slug</th><th>Levels</th><th>Required ⭐</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {worlds.map((w) => (
              <tr key={w.id} data-testid={`world-row-${w.slug}`}>
                <td>{w.sortOrder}</td>
                <td><b>{w.name}</b><br /><small>{w.description}</small></td>
                <td><code>{w.slug}</code></td>
                <td>{w.publishedLevelCount}/{w.levelCount}</td>
                <td>{w.requiredStars}</td>
                <td>{w.isPublished ? <span className="admin-badge admin-badge--published">published</span> : <span className="admin-badge admin-badge--draft">hidden</span>}</td>
                <td>
                  <BigButton variant="ghost" onClick={() => setEditing({ ...w })} data-testid={`world-edit-${w.slug}`}>Edit</BigButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="modal" onClick={() => setEditing(null)}>
          <div className="modal__panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620, background: "#fff", padding: "1.25rem", borderRadius: 16 }}>
            <h2 style={{ margin: 0 }}>Edit “{editing.name}”</h2>
            <div className="admin-form">
              <label>Name<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
              <label>Description<textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
              <div className="editor-field-row">
                <label>Sort order<input type="number" value={editing.sortOrder} onChange={(e) => setEditing({ ...editing, sortOrder: parseInt(e.target.value || "0") })} /></label>
                <label>Required ⭐<input type="number" value={editing.requiredStars} onChange={(e) => setEditing({ ...editing, requiredStars: parseInt(e.target.value || "0") })} /></label>
              </div>
              <label>Thumbnail URL<input value={editing.thumbnailUrl || ""} onChange={(e) => setEditing({ ...editing, thumbnailUrl: e.target.value })} /></label>
              <label>Map artwork URL<input value={editing.mapArtworkUrl || ""} onChange={(e) => setEditing({ ...editing, mapArtworkUrl: e.target.value })} /></label>
              <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
                <input type="checkbox" checked={!!editing.isPublished} onChange={(e) => setEditing({ ...editing, isPublished: e.target.checked })} />
                Published (visible to children)
              </label>
            </div>
            <div className="modal__actions" style={{ marginTop: "0.75rem" }}>
              <BigButton variant="ghost" onClick={() => setEditing(null)}>Cancel</BigButton>
              <BigButton onClick={save} data-testid="world-save">Save</BigButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function LevelManager() {
  const [levels, setLevels] = useState([]);
  const [worlds, setWorlds] = useState([]);
  const [filterWorld, setFilterWorld] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const navigate = useNavigate();

  async function reload() {
    setErr(null);
    try {
      const params = {};
      if (filterWorld) params.worldId = filterWorld;
      if (filterStatus) params.status = filterStatus;
      const [lvRes, wRes] = await Promise.all([
        api.get("/admin/levels", { params }),
        api.get("/admin/worlds"),
      ]);
      setLevels(lvRes.data.items || []);
      setWorlds(wRes.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { reload(); }, [filterWorld, filterStatus]);

  function toggle(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === levels.length) setSelected(new Set());
    else setSelected(new Set(levels.map((l) => l.id)));
  }

  async function bulk(action) {
    if (selected.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.size} level(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/levels/bulk-action", {
        levelIds: [...selected], action,
      });
      setMsg(`${action} → ${data.updated.length} level(s). ${data.warnings.length ? "Warnings: " + data.warnings.join("; ") : ""}`);
      setSelected(new Set());
      reload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="level-manager">
      <div className="admin-header">
        <div><h1>Levels</h1><p className="admin-header__sub">{levels.length} in view</p></div>
      </div>
      {err && <div className="admin-error">{err}</div>}
      {msg && <div className="admin-ok">{msg}</div>}

      <div className="admin-toolbar">
        <label>World
          <select value={filterWorld} onChange={(e) => setFilterWorld(e.target.value)} data-testid="level-filter-world">
            <option value="">All worlds</option>
            {worlds.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label>Status
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="level-filter-status">
            <option value="">Any</option>
            <option value="draft">draft</option>
            <option value="playable">playable</option>
            <option value="polished">polished</option>
          </select>
        </label>
        <div style={{ flex: 1 }} />
        <span>{selected.size} selected</span>
        <BigButton variant="ghost" disabled={selected.size === 0 || busy} onClick={() => bulk("publish")} data-testid="level-bulk-publish">Publish</BigButton>
        <BigButton variant="ghost" disabled={selected.size === 0 || busy} onClick={() => bulk("unpublish")} data-testid="level-bulk-unpublish">Unpublish</BigButton>
        <BigButton variant="ghost" disabled={selected.size === 0 || busy} onClick={() => bulk("duplicate")} data-testid="level-bulk-duplicate">Duplicate</BigButton>
        <BigButton variant="coral" disabled={selected.size === 0 || busy} onClick={() => bulk("delete")} data-testid="level-bulk-delete">Delete</BigButton>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th><input type="checkbox" checked={selected.size === levels.length && levels.length > 0} onChange={toggleAll} data-testid="level-select-all" /></th>
            <th>Level</th>
            <th>World</th>
            <th>Objects</th>
            <th>Diff.</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {levels.map((l) => (
            <tr key={l.id} className={selected.has(l.id) ? "selected" : ""} data-testid={`level-row-${l.slug}`}>
              <td><input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)} data-testid={`level-select-${l.slug}`} /></td>
              <td><b>{l.name}</b><br /><small><code>{l.slug}</code></small></td>
              <td>{l.worldName}</td>
              <td>{l.requiredObjectCount} req / {l.totalObjectCount} total</td>
              <td>{l.difficulty}</td>
              <td>{statusBadge(l)}</td>
              <td className="admin-actions">
                <BigButton variant="ghost" onClick={() => navigate(`/admin/levels/${l.id}/edit`)} data-testid={`level-edit-${l.slug}`}>Edit hitboxes</BigButton>
              </td>
            </tr>
          ))}
          {levels.length === 0 && <tr><td colSpan="7" style={{ color: "var(--wmleye-ink-soft)" }}>No levels match the filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
